/**
 * Просмотр фото на весь экран.
 *
 * Фото ребёнка — половина ценности ленты для родителя, поэтому смотреть их
 * нужно крупно, а не в миниатюре 80×80.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@/shared/ui/Icon'

import { haptic } from '@/shared/telegram'

export function PhotoViewer({
  urls,
  startIndex,
  onClose,
}: {
  urls: string[] | null
  startIndex: number
  onClose: () => void
}) {
  const [index, setIndex] = useState(startIndex)

  useEffect(() => setIndex(startIndex), [startIndex, urls])

  useEffect(() => {
    if (!urls) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, urls.length - 1))
      if (event.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0))
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [urls, onClose])

  if (!urls || urls.length === 0) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-black"
      style={{ animation: 'fade-in 160ms var(--ease-out-soft) both' }}
    >
      <div className="pt-safe flex items-center justify-between px-4 py-3 text-white">
        <span className="tabular text-note text-white/85">
          {index + 1} / {urls.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть"
          className="tap-target text-[21px]"
        >
          <Icon name="action-exit" size={20} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center px-2">
        <img
          src={urls[index]}
          alt=""
          className="max-h-full max-w-full object-contain"
        />
      </div>

      {urls.length > 1 && (
        <div className="pb-safe flex items-center justify-center gap-6 py-4 text-white">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => {
              haptic('select')
              setIndex((i) => Math.max(i - 1, 0))
            }}
            className="tap-target flex items-center justify-center disabled:opacity-30"
            aria-label="Предыдущее"
          >
            <Icon name="action-prev" size={24} />
          </button>
          <button
            type="button"
            disabled={index === urls.length - 1}
            onClick={() => {
              haptic('select')
              setIndex((i) => Math.min(i + 1, urls.length - 1))
            }}
            className="tap-target flex items-center justify-center disabled:opacity-30"
            aria-label="Следующее"
          >
            <Icon name="action-next" size={24} />
          </button>
        </div>
      )}
    </div>,
    document.body,
  )
}
